import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/7-small-business-owners-share-their-thoughts-on-community-support`;

  return (
    <MainLayout>
      <NextSeo
        title="7 small business owners share what the community's support has meant to them | DAYMADE"
        description="DAYMADE launches the partnership with Town & City to make it even easier to support your favourite local business"
        openGraph={{
          title:
            "7 small business owners share what the community's support has meant to them | DAYMADE",
          description:
            'DAYMADE launches the partnership with Town & City to make it even easier to support your favourite local business',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/7-small-business-owners-share-their-thoughts-on-community-support',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'Javelin Retail in Bury St Edmunds',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'support,local,business,community,partnership,Town and City,gift cards,giving back,impact,near me',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="Javelin Retail in Bury St Edmunds"
        heroImageYPosition="38%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            7 SMALL BUSINESS OWNERS SHARE WHAT{' '}
            <strong>THE COMMUNITY'S SUPPORT</strong> HAS MEANT FOR THEM
          </h1>

          <p className="subheading">
            DAYMADE launches the partnership with Town & City to make it even
            easier to support your favourite local business
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Abbi James
          </p>

          <p>
            <span className="bigFirstLetter">T</span>his month, DAYMADE and Town
            & City Gift Cards have joined forces to further support independent
            businesses. This partnership will enable DAYMADE players across the
            UK to support their local business of choice, from farmers markets
            to bougee boutiques.
          </p>

          <p>
            This last year has taught us all just how much we appreciate our
            local independent businesses - where would our community be without
            them! DAYMADE players have really turned UP for small business
            owners, by using their prize of choice to support their locals. We’d
            love to further this support, and so DAYMADE have decided to partner
            up with{' '}
            <a href="https://townandcitygiftcards.com/">
              Town & City Gift Cards
            </a>{' '}
            - a match made in heaven!
          </p>

          <p>
            You might be asking yourself:{' '}
            <b>what IS a Town and City Gift Card</b>? Well, this award-winning
            programme consists of local gift cards that can be used with a range
            of businesses in a particular area, from shops, bars and restaurants
            to theatres, salons and leisure venues. This isn’t just for major
            cities either, with over 60 different programmes operating in towns
            and cities across the UK and Ireland.
          </p>

          <p>
            If you live in or visit one of these places, you’re in luck –
            there’s a Town and City Gift Card for you: Aberdeen, Bury St
            Edmunds, Camberley, Canterbury, Chester, City of London, Chichester,
            Eastleigh, East Ayrshire, Edinburgh, Glasgow, Gloucester, Harrogate,
            Isle of Wight, Kirkcaldy, Knowsley, Minehead, Middlesbrough,
            Newport, Perth, Preston, REDS (Rural Scotland Gift Card), Salisbury,
            Scarborough, Southport, Sunderland and Tavistock.
          </p>

          <p>
            We’ve all felt the added pressures from varying lockdowns over the
            last 18 months, but it’s been especially difficult for independent
            businesses. Our new partnership has been designed to tap into the
            desire from younger consumers to support businesses through local
            gift cards and prioritise experiences. GCVA data found that it was
            younger consumers who were most motivated to support local
            businesses, with 25.5% of 25-44’s seeing gift cards as a mutually
            beneficial way to show that support for retailers and other venues
            where they live.
          </p>

          <p>
            One of our recent DAYMADE winners, Jamie Sparks, 26, chose the
            Exeter Town & City gift card as his £25 prize, hoping to make his
            next trip into Exeter all the more special:
          </p>

          <p>
            “I think it’s a really good prize, as it covers not just shopping
            but eating out too. And I think it helps the smaller chains and the
            independent shops that are local, helping reduce footprint. I
            discovered new, smaller, independent shops when looking through
            where I could use the gift card. I’m looking forward to using the
            card, it’s like a bonus and will make the next trip into the city
            more of a treat."
          </p>

          <p>
            We’ve loved hearing from small business owners how your support made
            a difference.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="Janet Thompson, the founder of Grown Up Marshmallows"
            className="singleBlogImage"
          />

          <p>
            Janet Thompson is the founder of Grown Up Marshmallows, a local
            artisan producer of gourmet marshmallows, and opened a shop on The
            Avenue in Minehead a week before the first lockdown:
          </p>

          <p>
            “Our product is created in small batches on Exmoor National Park,
            using fruit we’ve grown ourselves, and packaged with 100%
            recyclable, biodegradable, compostable material. Creating a local
            product was important to us. With the lockdown, we had to adapt our
            business, supplying hamper companies, refining our product and
            creating items like the toasting kit, and actually last year was our
            best year yet.
          </p>

          <p>
            “All of our boxes of marshmallow have the Exmoor flag, so they’re
            ideal for tourists but we’re really lucky to have experienced that
            local support too. Without locals, we don’t have a business. We hope
            that people will buy the Minehead Gift Card to show their support
            for our town.”
          </p>

          <p>
            There’s a huge array of businesses that accept Town and City Gift
            Cards, from artisan product designers, to cafes and restaurants
            serving up local produce. Another Minehead-based business set to
            benefit from the new partnership is the Alcombe Fish Bar.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="Jill Foster, from the Alcombe Fish Bar"
            className="singleBlogImage"
          />

          <p>Jill Foster, from the Alcombe Fish Bar:</p>

          <p>
            “I think the Minehead Gift Card is a great idea. I had someone ring
            me from Nottingham wanting to buy a voucher as a gift for family
            that live locally. The Minehead Gift Card is an easy way for someone
            to give a nice gift. Locals might also buy it for themselves, so
            they can treat themselves to a takeaway.
          </p>

          <p>
            “People are starting to put local as their first choice, and
            realising that they can get what they want locally, often cheaper
            too. The Minehead Gift Card is going to be especially important for
            us to support those businesses that have been closed during
            lockdown, it will be really beneficial for our area.”
          </p>

          <p>
            Local gift card schemes like the Minehead Gift Card are helping
            business owners to adjust and adapt following the pandemic. Yet
            another local business where the Minehead Gift Card can be spent is
            The Lighting Company, an established lighting and homeware business
            with a flagship store in Minehead.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="Glenn Ross, from The Lighting Company"
            className="singleBlogImage"
          />

          <p>Glenn Ross is from The Lighting Company:</p>

          <p>
            “As for all businesses, managing the uncertainty of lockdown has
            been a challenge. In this third lockdown, we’ve taken it as an
            opportunity to completely refresh our Minehead store, develop new
            ranges, work on photography and upskill staff - the majority of whom
            have been working throughou
          </p>

          <p>
            “We employ over 20 people locally, and are passionate about
            supporting Minehead. The Minehead Gift Card will keep money local,
            and that can only be a good thing. Because it can be spent with lots
            of different businesses in the area, there is that security too.
            Money spent locally stays local and that benefits everyone.
          </p>

          <p>
            “There has been an uptick in interest for British made products and
            after the first lockdown, that support from locals was fantastic.
            People are shopping here in Minehead rather than driving to other
            towns and cities.
          </p>

          <p>
            “Whilst online is a really important channel for us, a presence on
            the high street is vital. Customers want to look, feel and touch the
            products, guided by an experienced member of staff.”
          </p>

          <p>
            Glen really hit the nail on the head – local stores are able to
            offer something that nationwide conglomerates just aren’t able to,
            from a personalised customer service, to an innate passion for local
            produce.
          </p>

          <p>
            Scarborough joined the Town and City Gift Card programme in November
            2020, and now has a whopping 79 retailers that accept the
            Scarborough Town and City Gift Cards. This historic and vibrant town
            has a great selection of retailers to choose from, including Toyland
            Toyshop, Nomination, Cosmetique, Bike and Boot Hotel and Restaurant,
            Bay Tree Interiors, Geko Glass and Lilly’s Treasures. Karon Wallis,
            owner of Lilly’s Treasures said the Scarborough Gift Card is a way
            to show your support for local businesses:
          </p>

          <p>
            “The Scarborough Gift Card is absolutely the way forward. This year
            has shown just how important our communities are and our local
            shops. People know they can come into Lilly’s Treasures and get
            something unique. But more than that, it’s the personal connection.
            I know all of my customers by name, I try to show customers that I
            care, taking the time to talk which is good for our mental health
            and wellbeing. You don’t get that when you shop online.
          </p>

          <p>
            “When people buy with me, that money that goes back into the
            community. I have my crafters, I buy from the local butcher and use
            independent shops where I can. There are so many people that support
            a small business. We are all in this together. When you buy the
            Scarborough Gift Card as a gift, you are showing your support for
            your businesses and your town.
          </p>

          <p>
            “When lockdown happened, I’d just moved to the new shop but I took
            the time to build my community who have been so supportive. People
            say you have to go online to get unique items. We’re proving that it
            is on the high street, in Scarborough.”
          </p>

          <p>
            Gloucester, the Cathedral city of the Cotswolds, has over 2,000
            years of history to immerse yourself in. They launched the
            Gloucester Gift Card a year prior, in November 2019, and have 37
            local retailers signed up to the programme including the Museum of
            Gloucester and Café René. An experience-based business for you to
            spend your Gloucester Gift Card at is VRXPerience. Founder Marc
            Edden said the Gloucester Gift Card is a way to enjoy a whole day
            out in Gloucester:
          </p>

          <p>
            “We set up the business in November 2019, less than 4 months later
            Covid-19 hit. We have virtual reality headsets, escape rooms and
            racing. I found out about the Gloucester Gift Card on Facebook and
            signed up. I need to take every opportunity I can to advertise
            VRXPerience to new customers and reach people that we wouldn’t
            normally reach. Being a part of the Gloucester Gift Card exposes us
            to a new market outside of our regular customers.
          </p>

          <p>
            “In the same way, I hope that being a part of the programme will
            help our customers to discover new businesses too. With the
            Gloucester Gift Card, you can enjoy a whole day out, do some
            shopping in Gloucester, have something to eat, have a go at VR and
            much more. I set up the business myself from scratch, it has been a
            real labour of love. When people shop local and buy the Gloucester
            Gift Card, it will make such a difference to small businesses like
            mine. It’s make or break for us right now.”
          </p>

          <p>
            DAYMADE is all about the value of experiences over money, with
            experience gift cards being especially popular right now. GCVA found
            that 30.1% of Generation Z shoppers (16-24) have purchased an
            experience gift card in January 2021, and a massive 82.6% have
            purchased a gift card over the past three years. If you’re one of
            these people who chose to use their prize to support a local
            business, thank you, thank you, thank you!
          </p>

          <p>
            The benefit of these cards goes beyond money going to local
            businesses, as the scheme also provides local businesses with a
            network to help support one another in a community like never
            before. This can be especially important when businesses are in more
            rural areas, such as those in Perthshire, Scotland. The REDS Rural
            Gift Card can be spent with more than 120 rural businesses across
            Scotland, including a number of experiential businesses such as
            Progression Bikes in Dunkeld, Perthshire. Adam Flint from
            Progression Bikes said the REDS Rural Gift Card will help them to
            attract new customers as a community:
          </p>

          <p>
            “Being involved with our local business community firstly helps us
            as businesses to know more about what else is on offer in our area
            and secondly it helps members of the public to come and take
            advantage of what we’ve got here relatively easily. The REDS Rural
            Gift Card means we can get day trippers out from Glasgow or
            Edinburgh and they can come to places like this, hire an ebike and
            explore the varied trails right from our shop door. So people who
            don’t know the area well can come here and they can hire a bike in
            the morning, have lunch in one of the town’s lovely cafés and then
            they can go canyoning or stand up paddle boarding in the afternoon
            and we can organise the whole lot for them through our network of
            businesses.”
          </p>

          <p>
            The community spirit and importance of having a strong, supportive
            network was echoed by many independent businesses involved with the
            Town and City Gift Card programme.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="Jess Simms-Baalham, from Javelin Retail in Bury St Edmunds"
            className="singleBlogImage"
          />

          <p>Jess Simms-Baalham from Javelin Retail in Bury St Edmunds:</p>

          <p>
            “The gift card has been a success in-store thus far with a lot of
            customers eager to shop in a family run, local business after months
            of lockdown. As lockdown drove all non-essential retail online it
            seemed to signal the death of the high street. Instead the opposite
            is happening now! It seems shoppers are keen to get back to their
            local indies and support real-life people. Spending the gift card
            with us keeps the community spirit alive!”
          </p>

          <p>
            Now the question remains: how do you win a Town and City Gift Card
            to spend in your local businesses? If you match 3 main numbers in
            one of our Weekly Draws, you’ll bag yourself a £25 gift card for
            your chosen town or city. If you’re killin’ it and match 3 main
            numbers plus the bonus ball, you’ll walk away with a £100 Town and
            City Gift Card.
          </p>

          <p>
            We’re so excited to support local businesses up and down the
            country, so all you have to do to take part is click below and enter
            for your chance to win big and support local!
          </p>

          <Separator style={{ margin: '2em' }} />

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
